import React from 'react';
import { Button, Card, DotLoading, Empty, List, NavBar, Tabs } from 'antd-mobile';
import { useTranslation } from 'react-i18next';
import { referralPageStyles } from './styles';
import { ReferralViewState, RewardEventType } from './types';

interface ReferralViewProps {
  state: ReferralViewState;
  shareUrl: string;
  onCopy: () => Promise<void>;
  onRefresh: () => Promise<void>;
  onChildrenPrev: () => Promise<void>;
  onChildrenNext: () => Promise<void>;
  onRewardsPrev: () => Promise<void>;
  onRewardsNext: () => Promise<void>;
}

export const ReferralView: React.FC<ReferralViewProps> = ({
  state,
  shareUrl,
  onCopy,
  onRefresh,
  onChildrenPrev,
  onChildrenNext,
  onRewardsPrev,
  onRewardsNext,
}) => {
  const { t } = useTranslation(['referral', 'common']);

  // Resolve a reward-event label via i18n; fall back to the event key itself.
  const getEventLabel = (eventType: RewardEventType): string => {
    const key = `events.${eventType}`;
    const translated = t(key);
    return translated === key ? eventType : translated;
  };

  if (state.loading && !state.overview) {
    return (
      <div className="webview-page" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <DotLoading />
      </div>
    );
  }

  return (
    <div className="webview-page">
      <NavBar
        back={null}
        right={
          <Button size="mini" onClick={() => void onRefresh()}>
            {t('common:refresh')}
          </Button>
        }
      >
        {t('navTitle')}
      </NavBar>
      <div className="webview-section">
        <div className="webview-hero-card">
          <div style={{ fontSize: 14, color: 'rgba(23, 24, 28, 0.62)' }}>{t('inviteLink')}</div>
          <div style={{ fontSize: 22, lineHeight: 1.2, fontWeight: 700, marginTop: 10 }}>{t('inviteHeadline')}</div>
          <div style={{ ...referralPageStyles.linkBox, marginTop: 14 }}>{shareUrl || t('generating')}</div>
          <Button style={{ marginTop: 16 }} color="primary" onClick={() => void onCopy()} disabled={!shareUrl}>
            {t('copyLink')}
          </Button>
        </div>
      </div>
      <div className="webview-section" style={referralPageStyles.statGrid}>
        <div className="webview-stat-card">
          <div className="webview-right-meta">{t('totalPoints')}</div>
          <div>
            <div className="webview-stat-value">{state.overview?.total_points || 0}</div>
            <div className="webview-stat-label">{t('totalPointsLabel')}</div>
          </div>
        </div>
        <div className="webview-stat-card">
          <div className="webview-right-meta">{t('directChildren')}</div>
          <div>
            <div className="webview-stat-value">{state.overview?.direct_children_count || 0}</div>
            <div className="webview-stat-label">{t('directChildrenLabel')}</div>
          </div>
        </div>
        <div className="webview-stat-card">
          <div className="webview-right-meta">{t('registerReward')}</div>
          <div>
            <div className="webview-stat-value">{state.overview?.register_reward_points || 0}</div>
            <div className="webview-stat-label">{t('registerRewardLabel')}</div>
          </div>
        </div>
        <div className="webview-stat-card">
          <div className="webview-right-meta">{t('forwardReward')}</div>
          <div>
            <div className="webview-stat-value">{state.overview?.forward_reward_points || 0}</div>
            <div className="webview-stat-label">{t('forwardRewardLabel')}</div>
          </div>
        </div>
      </div>
      <div className="webview-section">
        <Card>
          <Tabs>
            <Tabs.Tab title={t('tabChildren')} key="children">
              {state.children.length === 0 ? (
                <Empty description={t('emptyChildren')} />
              ) : (
                <List>
                  {state.children.map((child) => (
                    <List.Item
                      key={child.id}
                      description={t('childDescription', {
                        total: child.total_points,
                        contributed: child.contributed_points,
                        forward: child.forward_reward_count,
                      })}
                      extra={new Date(child.bound_at).toLocaleDateString()}
                    >
                      {child.display_name} @{child.username}
                    </List.Item>
                  ))}
                </List>
              )}
              <div style={referralPageStyles.paginationRow}>
                <Button size="small" disabled={state.childrenPagination.page <= 1} onClick={() => void onChildrenPrev()}>
                  {t('common:prevPage')}
                </Button>
                <span style={{ fontSize: 12, color: '#8c8c8c' }}>
                  {state.childrenPagination.page} / {Math.max(state.childrenPagination.totalPages, 1)}
                </span>
                <Button
                  size="small"
                  disabled={state.childrenPagination.page >= Math.max(state.childrenPagination.totalPages, 1)}
                  onClick={() => void onChildrenNext()}
                >
                  {t('common:nextPage')}
                </Button>
              </div>
            </Tabs.Tab>
            <Tabs.Tab title={t('tabRewards')} key="rewards">
              {state.rewards.length === 0 ? (
                <Empty description={t('emptyRewards')} />
              ) : (
                <List>
                  {state.rewards.map((reward) => (
                    <List.Item
                      key={reward.id}
                      description={reward.source_display_name || reward.source_username || t('rewardSourceFallback')}
                      extra={<span className="webview-positive">+{reward.points}</span>}
                    >
                      {getEventLabel(reward.event_type)}
                    </List.Item>
                  ))}
                </List>
              )}
              <div style={referralPageStyles.paginationRow}>
                <Button size="small" disabled={state.rewardsPagination.page <= 1} onClick={() => void onRewardsPrev()}>
                  {t('common:prevPage')}
                </Button>
                <span style={{ fontSize: 12, color: '#8c8c8c' }}>
                  {state.rewardsPagination.page} / {Math.max(state.rewardsPagination.totalPages, 1)}
                </span>
                <Button
                  size="small"
                  disabled={state.rewardsPagination.page >= Math.max(state.rewardsPagination.totalPages, 1)}
                  onClick={() => void onRewardsNext()}
                >
                  {t('common:nextPage')}
                </Button>
              </div>
            </Tabs.Tab>
          </Tabs>
        </Card>
      </div>
    </div>
  );
};
